// The `choices` block: a decision the agent hands back to the user.
//
// For the tradeoffs a person owns rather than the mechanics an agent should judge
// itself. Keep iterating or move on, which threshold, what to fix first. Never one
// per split or per condition: the agent picks those, and a prompt per split turns a
// build into a form.
//
// A click sends its `message` as an ordinary turn, so a button is feedback to the
// agent and never a tool call. That is the whole reason `message` is a field of its
// own rather than derived from the label: the label wants to be short and the
// instruction wants to be unambiguous, and one string cannot be both.
//
// One option is a valid block. A single "go ahead" is the commonest shape there is,
// and the agent asking it in prose leaves the user retyping the answer.

/** One thing the user can choose. */
export interface Choice {
  /** What the button says. */
  label: string;
  /** Sent verbatim as the user's next turn when the button is pressed. */
  message: string;
}

export interface Choices {
  /** What is being asked. May be empty when the buttons speak for themselves. */
  question: string;
  options: Choice[];
}

/**
 * How many options a block may offer.
 *
 * Capped because this is a fork, not a menu: past a handful the agent is asking the
 * user to do its judging for it, which is the failure this block is meant to avoid.
 * Anything beyond the cap is dropped rather than the block being rejected, so an
 * over-eager turn still renders the first few.
 */
export const MAX_CHOICES = 4;

/**
 * Read a choices block out of a body, or null when it is not one.
 *
 * Null falls through to the markdown fallback, so a malformed block reads as text
 * rather than vanishing. Every rejection here has to be real: no options is not a
 * decision, and an option that cannot say what pressing it does is not an option.
 */
export function parseChoices(raw: string): Choices | null {
  let doc: unknown;
  try {
    doc = JSON.parse(raw);
  } catch {
    return null;
  }
  if (typeof doc !== 'object' || doc === null) return null;
  const record = doc as Record<string, unknown>;
  const rawOptions = record.options;
  if (!Array.isArray(rawOptions)) return null;

  const options: Choice[] = [];
  for (const item of rawOptions) {
    if (options.length >= MAX_CHOICES) break;
    // A bare string is both the label and the instruction, which is right for the
    // "go ahead" case and the only shape where one string can serve both.
    if (typeof item === 'string') {
      const text = item.trim();
      if (text) options.push({ label: text, message: text });
      continue;
    }
    if (typeof item !== 'object' || item === null) continue;
    const fields = item as Record<string, unknown>;
    const label = typeof fields.label === 'string' ? fields.label.trim() : '';
    const message =
      typeof fields.message === 'string' ? fields.message.trim() : '';
    // A label with no message would send nothing; a message with no label would be
    // an unreadable button. Either way there is nothing to render.
    if (!label || !message) continue;
    options.push({ label, message });
  }
  if (options.length === 0) return null;

  return {
    question: typeof record.question === 'string' ? record.question.trim() : '',
    options,
  };
}

/** Everything that can hold a live choices block back, as the panel knows it. */
export interface ChoicesState {
  /** A turn is running right now. */
  asking: boolean;
  /** Studio is still assembling this model as a copy of another. */
  isDuplicating: boolean;
  /** This model's dataset exists and is not still building. */
  isReady: boolean;
  /** The turn's offer belongs to a DIFFERENT model than the one on screen. */
  offersElsewhere: boolean;
  /** The conversation ran under the agent for the model's OTHER base: read-only. */
  otherBase?: boolean;
}

/**
 * Why the newest turn's choices cannot be pressed, or undefined when they can.
 *
 * One expression for both halves of the question. `canChoose` used to be computed
 * beside the caption from the same three conditions written out twice, and they
 * drifted: only one of the three supplied a reason, so two of them drew a disabled
 * button with no tooltip, no caption, and nothing on screen to explain it. A disabled
 * control with no reason reads as broken, and one of these lands at the end of a
 * duplication, where "nothing happens when I click" is the least useful thing the page
 * could say.
 *
 * The order is the order the reader would ask in: the nearest wait first. Duplication
 * before dataset matters most, and is the one that was wrong. A fresh copy is held by a
 * duplication that takes MINUTES, while `isReady` is about a dataset build that takes
 * hours, so a copy was telling people to come back when the dataset finished building:
 * the wrong wait, and off by two orders of magnitude, on the one page where somebody is
 * actually sitting there waiting.
 *
 * Says nothing about a LIVE model on purpose. That one is read-only rather than
 * unusable: the page is read-only on it too, and a change refused on a live model comes
 * back with the offer to copy it, which is the most useful answer in the whole flow.
 *
 * Callers apply this to the NEWEST turn only. An older block is settled rather than
 * waiting, and telling somebody it is waiting on a dataset would not be true.
 */
export function whyHeldBack(state: ChoicesState): string | undefined {
  if (state.asking) return 'Available when this turn finishes';
  // Before the rest: nothing about this conversation can be acted on any more, only
  // carried over, and the banner above the box says how.
  if (state.otherBase) return 'Continue this conversation to act on it';
  if (state.isDuplicating)
    return 'Available when the copy finishes building, usually a few minutes';
  if (!state.isReady) return 'Available once the dataset finishes building';
  // A turn that duplicates a live model ends by offering to make the change on the
  // copy, and that offer renders on the ORIGINAL, where pressing it can only earn a
  // refusal: a change has to name the model the reader has open. The jump chip beside
  // it is the way there, and the block comes back to life once the conversation
  // follows.
  if (state.offersElsewhere) return 'Answer this on the copy';
  return undefined;
}
